import React from 'react'
import styled from 'styled-components'

import AppMap, { AppMapProps } from 'components/AppMap'
import AppMarkerServiceLine from 'components/AppMarkerServiceLine'
import AppMarkerStat from 'components/AppMarkerStat'
import ServiceLineMaterialBadge from 'components/ServiceLineMaterialBadge'
import { AssetItem, AssetMapCluster } from 'generated/graphql'
import { Bounds } from 'hooks/useMapBounds'
import ServiceLineLegendIcon from './ServiceLineLegendIcon'
export interface ServiceLineMapProps extends Omit<AppMapProps, 'children'> {
  assetOpen: string
  bounds: Bounds
  clusters: AssetMapCluster[]
  mapCenter: null | google.maps.LatLng
  points: AssetItem[]
  setAssetOpen: (value: string) => void
  updateBounds(): void
}

const ServiceLineMap = (props: ServiceLineMapProps) => {
  const {
    assetOpen,
    bounds,
    clusters,
    mapCenter,
    onLoad,
    onUnmount,
    points,
    setAssetOpen,
    updateBounds
  } = props

  if (!mapCenter) {
    return null
  }

  const renderPoint = (point: AssetItem) => {
    const { location, serviceLineAsset } = point
    const {
      privateMaterial,
      privateVerifiedDate,
      publicMaterial,
      publicVerifiedDate
    } = serviceLineAsset || {}
    const {
      addressLine1: address,
      id: locationId,
      latitude,
      longitude
    } = location

    const position: google.maps.LatLngLiteral = {
      lat: latitude ?? 0,
      lng: longitude ?? 0
    }

    return (
      <AppMarkerServiceLine
        address={address}
        assetId={point.id}
        assetOpen={assetOpen}
        key={`${position.lat}${position.lng}`}
        locationId={Number(locationId)}
        position={position}
        privateMaterial={privateMaterial}
        privateVerifiedDate={privateVerifiedDate}
        publicMaterial={publicMaterial}
        publicVerifiedDate={publicVerifiedDate}
        setAssetOpen={setAssetOpen}
      />
    )
  }

  const renderCluster = (cluster: AssetMapCluster) => {
    const position: google.maps.LatLngLiteral = {
      lat: cluster.latitude ?? 0,
      lng: cluster.longitude ?? 0
    }

    const nonLead = cluster.nonLeadAssets ?? 0
    const lead = cluster.leadAssets ?? 0
    const unknown = cluster.unknownAssets ?? 0
    const galvanized = cluster.galvanizedAssets ?? 0
    const total = nonLead + lead + unknown + galvanized

    return (
      <AppMarkerStat
        error={lead}
        key={`${position.lat}${position.lng}${total}`}
        label={`${total}`}
        position={position}
        total={total}
        warning={unknown}
        galvanized={galvanized}
      />
    )
  }

  return (
    <div>
      <AppMap
        center={mapCenter}
        onBoundsChanged={updateBounds}
        onLoad={onLoad}
        onUnmount={onUnmount}
        zoom={bounds.zoomLevel}
        {...props}>
        {points.map(renderPoint)}
        {clusters.map(renderCluster)}
      </AppMap>

      <MapLegend>
        <ServiceLineLegendIcon />
        <div>
          <div style={{ textAlign: 'left' }}>
            <strong style={{ marginRight: '7px' }}>
              Service Line Material:
            </strong>
            <ServiceLineMaterialBadge color="#00B5E2" material="Non-lead" />
            <ServiceLineMaterialBadge color="#F7685B" material="Lead" />
            <ServiceLineMaterialBadge color="#FFC72C" material="Galvanized" />
            <ServiceLineMaterialBadge color="#0033A0" material="Unknown" />
          </div>
        </div>
      </MapLegend>
    </div>
  )
}

const MapLegend = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: calc(var(--gap) * 0.5);
  color: ${p => p.theme.colors.black50};

  > * {
    margin: 0.25em;
  }
`

export default ServiceLineMap
